class CustomSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Wait for the next tick to ensure child elements are fully parsed
    setTimeout(() => {
      this.track = this.querySelector('.custom-slider-track');
      if (!this.track) return;
      
      this.prevBtn = this.querySelector('.custom-slider-prev');
      this.nextBtn = this.querySelector('.custom-slider-next');
      this.dots = this.querySelectorAll('.custom-slider-dot');
      
      this.autoPlayEnabled = this.dataset.autoplay === 'true';
      this.autoPlaySpeed = parseInt(this.dataset.autoplaySpeed) || 5000;
      this.autoPlayInterval = null;
      this.scrollAmount = parseInt(this.dataset.scrollAmount) || 0;
      
      // For infinite looping logic (specifically used in featured collection slider)
      this.isLoop = this.dataset.loop === 'true';
      
      this.init();
    }, 0);
  }

  init() {
    this.updateUI();
    
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollPrev();
      });
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollNext();
      });
    }
    
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(index);
      });
    });
    
    let scrollTimeout;
    this.track.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => this.updateUI());
      
      // Debounced boundary reset for manual swiping in infinite loop mode
      if (this.isLoop) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const halfScrollWidth = this.track.scrollWidth / 2;
          if (this.track.scrollLeft >= halfScrollWidth) {
            this.track.scrollLeft = this.track.scrollLeft - halfScrollWidth;
          } else if (this.track.scrollLeft <= 0) {
            this.track.scrollLeft = this.track.scrollLeft + halfScrollWidth;
          }
        }, 150);
      }
    });
    
    if (this.autoPlayEnabled) {
      this.startAutoplay();
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      this.addEventListener('touchend', () => this.startAutoplay(), { passive: true });
    }
  }

  getScrollDistance() {
    if (this.scrollAmount > 0) return this.scrollAmount;
    const child = this.track.firstElementChild;
    if (!child) return 0;
    
    const gapStyle = window.getComputedStyle(this.track).gap;
    const gap = (gapStyle && gapStyle !== 'normal') ? parseInt(gapStyle) : 0;
    
    return child.offsetWidth + (isNaN(gap) ? 0 : gap);
  }

  scrollPrev() {
    this.stopAutoplay();
    const distance = this.getScrollDistance();
    if (!distance) return;

    if (this.isLoop) {
      const halfScrollWidth = this.track.scrollWidth / 2;
      if (this.track.scrollLeft <= 5) {
        this.track.scrollLeft = this.track.scrollLeft + halfScrollWidth;
      }
    }
    this.track.scrollBy({ left: -distance, behavior: 'smooth' });
    if (this.autoPlayEnabled) this.startAutoplay();
  }

  scrollNext() {
    this.stopAutoplay();
    const distance = this.getScrollDistance();
    if (!distance) return;

    if (this.isLoop) {
      const halfScrollWidth = this.track.scrollWidth / 2;
      if (this.track.scrollLeft >= halfScrollWidth - 5) {
        this.track.scrollLeft = this.track.scrollLeft - halfScrollWidth;
      }
      this.track.scrollBy({ left: distance, behavior: 'smooth' });
    } else {
      // Normal next behavior
      if (this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 5) {
          if (this.autoPlayEnabled) {
             this.track.scrollTo({ left: 0, behavior: 'smooth' });
          }
      } else {
          this.track.scrollBy({ left: distance, behavior: 'smooth' });
      }
    }
    if (this.autoPlayEnabled) this.startAutoplay();
  }

  goToSlide(index) {
    this.stopAutoplay();
    const child = this.track.children[index];
    if (child) {
        const trackRect = this.track.getBoundingClientRect();
        const childRect = child.getBoundingClientRect();
        const scrollTarget = child.offsetLeft - this.track.offsetLeft - (this.track.clientWidth - child.clientWidth) / 2;
        this.track.scrollTo({ left: scrollTarget, behavior: 'smooth' });
    }
    if (this.autoPlayEnabled) this.startAutoplay();
  }

  updateUI() {
    const scrollPos = this.track.scrollLeft;
    
    // Update dots
    if (this.dots.length > 0) {
      const slideWidth = this.track.clientWidth;
      let currentIndex = Math.round(scrollPos / slideWidth);
      if (currentIndex >= this.dots.length) currentIndex = this.dots.length - 1;
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === currentIndex);
      });
    }
    
    // Update buttons
    if (this.prevBtn) {
      this.prevBtn.disabled = scrollPos <= 1 && !this.isLoop;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = (scrollPos + this.track.clientWidth >= this.track.scrollWidth - 1) && !this.isLoop;
    }
  }

  startAutoplay() {
    if (!this.autoPlayEnabled) return;
    if (this.autoPlayInterval) clearInterval(this.autoPlayInterval);
    this.autoPlayInterval = setInterval(() => {
      this.scrollNext();
    }, this.autoPlaySpeed);
  }

  stopAutoplay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }
}

if (!customElements.get('custom-slider')) {
  customElements.define('custom-slider', CustomSlider);
}
